import React, { useState, useContext } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Typography,
  Checkbox,
  ListItemText,
} from '@material-ui/core';

import {
  wavelyticsQuizPartOne,
  wavelyticsQuizFullControl,
  wavelyticsQuizMinimal,
} from '../../data/wavelyticsQuiz';

import FormContext from '../../context/FormContext';
import PardotContext from '../../context/PardotContext';

const DATA_ONLY = 'Data Factory';
const BENCHMARKING = 'Benchmarking + Advanced Analytics + Self Serve Analytics';
const NONBENCHMARKING = 'Advanced Analytics + Self Serve Analytics';

const FULL_CONTROL_RESPONSE =
  'High, we want full control over our data modeling and outputs';
const BENCHMARKING_RESPONSE =
  'To Compare Performance against industry benchmarks or customer trends';

export const WavelyticsQuiz = ({ setQuizCompleted }) => {
  const [step, setStep] = useState(0);
  const [activeQuiz, setActiveQuiz] = useState({
    quiz: wavelyticsQuizPartOne,
    id: 0,
  });
  const [endedEarly, setEndedEarly] = useState(false);
  const [recommendation, setRecommendation] = useState(null);

  const { wavelyticsAnswers, handleWavelyticsAnswers } =
    useContext(FormContext);
  const { setWavelyticsParsedAnswers } = useContext(PardotContext);

  const isMulti = activeQuiz.id === 2 && step === 0;
  const currentStep = activeQuiz.id === 0 ? step : step + 3;
  const rawAnswer = wavelyticsAnswers.find(
    (a) => a.step === currentStep
  )?.answer;

  const selectValue = isMulti
    ? Array.isArray(rawAnswer)
      ? rawAnswer
      : []
    : typeof rawAnswer === 'string'
    ? rawAnswer
    : '';

  const handleSetStep = () => {
    const currentAnswer = wavelyticsAnswers[currentStep]?.answer;

    // End of part one – determine path
    if (activeQuiz.id === 0 && step === wavelyticsQuizPartOne.length - 1) {
      const controlAnswer = wavelyticsAnswers.find((a) => a.step === 2)?.answer;

      const isFullControl = controlAnswer === FULL_CONTROL_RESPONSE;

      setActiveQuiz({
        quiz: isFullControl ? wavelyticsQuizFullControl : wavelyticsQuizMinimal,
        id: isFullControl ? 1 : 2,
      });
      setStep(0);
      return;
    }

    // Full control quiz completed – recommend data-only
    if (activeQuiz.id === 1) {
      setRecommendation(DATA_ONLY);
    }

    // Early exit conditions for minimal quiz
    if (activeQuiz.id === 2) {
      if (
        step === 0 &&
        Array.isArray(currentAnswer) &&
        BENCHMARKING_RESPONSE.includes(currentAnswer)
      ) {
        setEndedEarly(true);
        setRecommendation(BENCHMARKING);
        return;
      }

      if (step === 1) {
        if (currentAnswer === 'Yes') {
          setEndedEarly(true);
          setRecommendation(BENCHMARKING);
          return;
        } else if (currentAnswer === 'No') {
          setEndedEarly(true);
          setRecommendation(NONBENCHMARKING);
          return;
        }
      }
    }

    // Advance step by default
    setStep((prev) => prev + 1);
  };

  const handleQuizCompleted = () => {
    setQuizCompleted(true);
    const parsedAnswer = wavelyticsAnswers
      .map(
        (answer) =>
          `${answer.question}: ${
            Array.isArray(answer.answer)
              ? answer.answer.join(', ')
              : answer.answer
          }`
      )
      .join(' | ');
    setWavelyticsParsedAnswers(parsedAnswer);
  };

  if (!activeQuiz.quiz[step] || endedEarly) {
    return (
      <Grid container style={{ padding: '4rem', minHeight: '275px' }}>
        <Grid item xs={12}>
          <Typography
            variant='h4'
            align='center'
            color='primary'
            style={{ minHeight: '85px' }}
          >
            {recommendation ? recommendation : 'Out Of Questions'}
          </Typography>
        </Grid>

        <Grid item xs={12} container justifyContent='center'>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={handleQuizCompleted}
            style={{ marginTop: '2rem' }}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    );
  }

  const unanswered = !wavelyticsAnswers[currentStep]?.answer && !endedEarly;
  return (
    <form style={{ padding: '3rem' }}>
      <Grid container spacing={2} justifyContent='center'>
        <Grid item xs={12} lg={8} style={{ minHeight: '85px' }}>
          <Typography variant='h4' align='center' color='primary'>
            {activeQuiz.quiz[step].question}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id='wavelytics-select-label'>
              {isMulti ? 'Select All That Apply' : 'Select An Option'}
            </InputLabel>
            <Select
              labelId='wavelytics-select-label'
              multiple={isMulti}
              value={selectValue}
              onChange={(e) =>
                handleWavelyticsAnswers(
                  e.target.value,
                  step,
                  activeQuiz,
                  activeQuiz.quiz[step].question
                )
              }
              renderValue={(selected) =>
                isMulti ? selected.join(', ') : selected
              }
            >
              {activeQuiz.quiz[step].answers.map((option) => (
                <MenuItem
                  key={option.id ?? option}
                  value={option.answer ?? option}
                >
                  {isMulti && (
                    <Checkbox
                      checked={selectValue.includes(option.answer ?? option)}
                    />
                  )}
                  <ListItemText primary={option.answer ?? option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} container justifyContent='center'>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={handleSetStep}
            style={{ marginTop: '2rem' }}
            disabled={unanswered}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};
